import {
  getGroupPerLessonDefaultsFromGroup,
  getGroupPerLessonLinkFieldDefaults,
} from '../../../GroupPerLessonLinkFields/groupPerLessonLink.utils'
import {
  getIndividualLessonLinkFieldDefaults,
  getIndividualLessonLinkTotal,
} from '../../../IndividualLessonLinkFields/individualLessonLink.utils'
import {
  isGroupPerLessonLinkTariff,
  isIndividualLessonLinkTariff,
  isLessonPaymentTariff,
} from '../../../../tariffTypes'
import { getStreamOneTimeDefaultPrice } from '../../../StreamOneTimeLinkFields/streamOneTimeLink.utils'
import type { ICourseEnrollmentItem } from './types'

type UseLessonAdditionalCourseFormParams = {
  order: ICourseEnrollmentItem
  selectedTariff?: ICourseTariff | null
  defaultDiscount?: number | null
  onChange: (
    key: keyof ICourseEnrollmentItem,
    value: number | string | boolean | null | object,
  ) => void
}

type GroupMeta = {
  lessonDuration?: number | null
  studentLessonPrice?: number | null
}

/**
 * Оркестрация доп. hour / minute / group_per_lesson:
 * applyTariffSelection, sync price, фиксация кол-ва, группа.
 */
export const useLessonAdditionalCourseForm = ({
  order,
  selectedTariff,
  defaultDiscount = null,
  onChange,
}: UseLessonAdditionalCourseFormParams) => {
  const isActive = isLessonPaymentTariff(selectedTariff?.type)
  const isIndividual = isIndividualLessonLinkTariff(selectedTariff?.type)
  const isGroupPerLesson = isGroupPerLessonLinkTariff(selectedTariff?.type)
  const isValueFixed = Boolean(order.is_value_fixed)
  const lessonCount = Math.max(
    1,
    Math.min(100, Math.trunc(Number(order.value) || 1)),
  )
  const suggestedPrice = getIndividualLessonLinkTotal(
    order.first_price,
    lessonCount,
  )

  const clearLessonFields = () => {
    onChange('group_id', null)
    onChange('lesson_duration', null)
    onChange('first_price', null)
    onChange('teacher_id', null)
    onChange('salary_per_hour', null)
    onChange('is_value_fixed', false)
  }

  const syncSuggestedPrice = (
    firstPrice: number | null,
    value: number | null,
    discount: number | null = null,
  ) => {
    const base = getIndividualLessonLinkTotal(firstPrice, value)
    const discountValue =
      isGroupPerLesson && !isValueFixed
        ? Math.max(0, Math.trunc(Number(discount ?? order.discount) || 0))
        : 0
    onChange('price', Math.max(0, base - discountValue))
  }

  const applyTariffSelection = (tariff: ICourseTariff) => {
    clearLessonFields()

    if (isIndividualLessonLinkTariff(tariff.type)) {
      const defaults = getIndividualLessonLinkFieldDefaults(tariff)
      const nextCount = 1
      // hour/minute: без фиксации итого расчётное; скидка недоступна (только gpl).
      onChange('discount', null)
      onChange('value', nextCount)
      onChange('period_unit', null)
      onChange('lesson_duration', defaults.lesson_duration)
      onChange('first_price', defaults.first_price)
      onChange(
        'price',
        getIndividualLessonLinkTotal(defaults.first_price, nextCount),
      )
      onChange('apply_discount_each_payment', false)
      onChange('is_period_fixed', false)
      onChange('is_value_fixed', false)
      return
    }

    if (isGroupPerLessonLinkTariff(tariff.type)) {
      const defaults = getGroupPerLessonLinkFieldDefaults(tariff)
      const nextCount = 1
      // Без фиксации кол-ва цена locked → скидку можно (предзаполнение).
      const nextDiscount = defaultDiscount ?? null
      onChange('discount', nextDiscount)
      onChange('value', nextCount)
      onChange('period_unit', null)
      onChange('lesson_duration', defaults.lesson_duration)
      onChange('first_price', defaults.first_price)
      onChange(
        'price',
        Math.max(
          0,
          getIndividualLessonLinkTotal(defaults.first_price, nextCount) -
            Math.max(0, Math.trunc(Number(nextDiscount) || 0)),
        ),
      )
      onChange('apply_discount_each_payment', false)
      onChange('is_period_fixed', false)
      onChange('is_value_fixed', false)
    }
  }

  const applyStreamOneTimeSelection = (tariff: ICourseTariff) => {
    clearLessonFields()
    // Цена редактируется → скидку не применяем (сумму можно снизить вручную).
    onChange('discount', null)
    onChange('value', null)
    onChange('period_unit', null)
    onChange('price', getStreamOneTimeDefaultPrice(tariff))
    onChange('apply_discount_each_payment', false)
    onChange('is_period_fixed', false)
  }

  const onChangeGroup = (groupId: number | null, meta?: GroupMeta | null) => {
    onChange('group_id', groupId)
    if (!isGroupPerLesson) return

    if (groupId == null) {
      const defaults = getGroupPerLessonLinkFieldDefaults(selectedTariff)
      onChange('lesson_duration', defaults.lesson_duration)
      onChange('first_price', defaults.first_price)
      syncSuggestedPrice(defaults.first_price, lessonCount)
      return
    }

    const defaults = getGroupPerLessonDefaultsFromGroup(selectedTariff, {
      lessonDuration: meta?.lessonDuration,
      studentLessonPrice: meta?.studentLessonPrice,
    })
    onChange('lesson_duration', defaults.lesson_duration)
    onChange('first_price', defaults.first_price)
    syncSuggestedPrice(defaults.first_price, lessonCount)
  }

  /** «Заполнить» из тарифа (без группы). */
  const applyGroupPerLessonFromTariff = () => {
    const defaults = getGroupPerLessonLinkFieldDefaults(selectedTariff)
    onChange('lesson_duration', defaults.lesson_duration)
    onChange('first_price', defaults.first_price)
    syncSuggestedPrice(defaults.first_price, lessonCount)
  }

  /** «Заполнить» только цену из группы (длительность уже из группы). */
  const applyGroupPerLessonPriceFromGroup = (meta?: GroupMeta | null) => {
    const defaults = getGroupPerLessonDefaultsFromGroup(selectedTariff, {
      lessonDuration: meta?.lessonDuration,
      studentLessonPrice: meta?.studentLessonPrice,
    })
    onChange('first_price', defaults.first_price)
    syncSuggestedPrice(defaults.first_price, lessonCount)
  }

  const onChangeDuration = (value: number | null) => {
    if (value == null) {
      onChange('lesson_duration', null)
      return
    }
    onChange('lesson_duration', value)
  }

  const onChangeFirstPrice = (value: number | null) => {
    if (value == null) {
      onChange('first_price', null)
      syncSuggestedPrice(null, lessonCount)
      return
    }
    onChange('first_price', value)
    syncSuggestedPrice(value, lessonCount)
  }

  const onToggleValueFixed = () => {
    const next = !isValueFixed
    onChange('is_value_fixed', next)
    onChange('value', lessonCount)
    // Фиксация → цена вручную → скидка недоступна.
    if (next) {
      onChange('discount', null)
      onChange('apply_discount_each_payment', false)
      syncSuggestedPrice(order.first_price ?? null, lessonCount, 0)
      return
    }
    syncSuggestedPrice(order.first_price ?? null, lessonCount)
  }

  const onChangeLessonCount = (value: number) => {
    const next = Math.max(1, Math.min(100, Number(value) || 1))
    onChange('value', next)
    syncSuggestedPrice(order.first_price ?? null, next)
  }

  const onChangePrice = (value: number | null) => {
    // Ручной итого только при зафиксированном кол-ве.
    if (!isValueFixed) return
    if (value === null || value === undefined) {
      onChange('price', null)
      return
    }
    onChange('price', Math.max(0, Number(value) || 0))
  }

  const onChangeDiscount = (value: string | number | null) => {
    if (!isGroupPerLesson || isValueFixed) return
    if (value === null || value === undefined || value === '') {
      onChange('discount', null)
      onChange('apply_discount_each_payment', false)
      syncSuggestedPrice(order.first_price ?? null, lessonCount, 0)
      return
    }
    const nextDiscount = Math.max(0, Math.trunc(Number(value) || 0))
    onChange('discount', nextDiscount || null)
    onChange('apply_discount_each_payment', false)
    syncSuggestedPrice(order.first_price ?? null, lessonCount, nextDiscount)
  }

  const onChangeTeacher = (
    teacherId: number | null,
    salaryPerHour: number | null,
  ) => {
    onChange('teacher_id', teacherId)
    onChange('salary_per_hour', salaryPerHour)
  }

  const discountValue = Math.max(0, Math.trunc(Number(order.discount) || 0))
  const netSuggestedPrice = Math.max(
    0,
    suggestedPrice -
      (isGroupPerLesson && !isValueFixed ? discountValue : 0),
  )

  return {
    isActive,
    isIndividual,
    isGroupPerLesson,
    isValueFixed,
    /** Итого editable только при is_value_fixed. */
    isPriceEditable: isValueFixed,
    /** gpl без фиксации: скидка доступна. */
    isDiscountEditable: isGroupPerLesson && !isValueFixed,
    discountValue,
    lessonCount,
    suggestedPrice: netSuggestedPrice,
    clearLessonFields,
    applyTariffSelection,
    applyStreamOneTimeSelection,
    onChangeGroup,
    applyGroupPerLessonFromTariff,
    applyGroupPerLessonPriceFromGroup,
    onChangeDuration,
    onChangeFirstPrice,
    onToggleValueFixed,
    onChangeLessonCount,
    onChangePrice,
    onChangeDiscount,
    onChangeTeacher,
  }
}
